import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DROPPED_QUERY_PARAMS_STATE_KEY } from '@/hooks/preservedNavigationConstants';
import { NON_PRESERVED_QUERY_PARAMS } from '@/constants/nonPreservedQueryParams';

/**
 * Routes where query parameters should NOT be automatically preserved.
 * Auth flows manage their own parameters and must not have env/other
 * params restored into them.
 */
const SKIP_ROUTES = new Set([
  '/login',
  '/login/redirect.html',
  '/oauth/callback',
  '/github/callback',
]);

/**
 * Reads the dropped-params marker out of React Router's `location.state`,
 * if the most recent navigation came through one of the preserved-navigation
 * primitives with a `dropParams` option. Returns an empty array when no
 * params were intentionally dropped.
 */
function getDroppedParams(state: unknown): readonly string[] {
  if (!state || typeof state !== 'object') {
    return [];
  }
  const value = (state as Record<string, unknown>)[DROPPED_QUERY_PARAMS_STATE_KEY];
  return Array.isArray(value) ? value.filter((k): k is string => typeof k === 'string') : [];
}

/**
 * Safety-net component that watches navigation and restores query parameters
 * that were present in the previous location but dropped by the new one.
 *
 * This catches all existing `useNavigate` / `<Link>` / `<Navigate>` call-sites
 * that don't use the Preserved* wrappers, without requiring migration.
 *
 * Mount once inside `<BrowserRouter>`, as a sibling of your route tree.
 */
export const QueryParamPreserver = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // First-load on an auth route would otherwise seed the ref with OAuth
  // secrets (e.g. `?code=...&state=...`); the effect below skip-returns on
  // auth routes, so without this guard the next non-auth navigation would
  // restore those secrets onto a normal app URL.
  const prevSearchRef = useRef(SKIP_ROUTES.has(location.pathname) ? '' : location.search);

  useEffect(() => {
    const currentSearch = location.search;
    const prevSearch = prevSearchRef.current;

    // Skip auth routes — they manage their own params. We MUST check this
    // BEFORE updating prevSearchRef, otherwise auth-flow search strings
    // (e.g. /oauth/callback?code=...&state=...) would be recorded and then
    // restored onto the next non-auth navigation, leaking OAuth secrets
    // into normal app URLs and telemetry.
    //
    // We deliberately do NOT clear prevSearchRef here — the whole point of
    // this safety net is to preserve params from before the auth route
    // (e.g. ?env=staging) across the auth round-trip.
    if (SKIP_ROUTES.has(location.pathname)) {
      return;
    }

    // Update ref for next navigation (only on non-skip routes)
    prevSearchRef.current = currentSearch;

    // If there was nothing to preserve, bail early
    if (!prevSearch) {
      return;
    }

    const prevParams = new URLSearchParams(prevSearch);
    const currentParams = new URLSearchParams(currentSearch);

    // Params the caller intentionally dropped — never restore these. The
    // NON_PRESERVED set covers one-shot action params (e.g. `applyTemplate`)
    // that must be consumed once and not restored onto later navigations
    // (issue #2688); `location.state` covers per-call `dropParams`.
    const droppedParams = new Set<string>([
      ...getDroppedParams(location.state),
      ...NON_PRESERVED_QUERY_PARAMS,
    ]);

    // Check if any previous params were unintentionally dropped
    let needsRestore = false;
    const merged = new URLSearchParams(currentParams);

    for (const [key, value] of prevParams) {
      if (!currentParams.has(key) && !droppedParams.has(key)) {
        merged.set(key, value);
        needsRestore = true;
      }
    }

    if (needsRestore) {
      const restoredSearch = `?${merged.toString()}`;
      navigate(
        { pathname: location.pathname, search: restoredSearch, hash: location.hash },
        // Preserve `location.state` so callers that rely on router state
        // (e.g. MSAL redirect-from, return-to-after-action chips) don't
        // lose it when the safety net runs.
        { replace: true, state: location.state },
      );
    }
  }, [location, navigate]);

  return null;
};
